import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/category/电影", label: "电影" },
  { to: "/category/电视剧", label: "电视剧" },
  { to: "/category/综艺", label: "综艺" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const { favorites } = useFavorites();

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-[1280px] items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-play-fill text-xl" aria-hidden="true" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            国产在线观看
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-3 md:flex">
          <form onSubmit={submitSearch} className="relative" role="search">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称…"
              aria-label="搜索影视名称"
              className="h-9 w-52 rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 lg:w-64"
            />
          </form>
          <Link
            to="/favorites"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-heart-3-line text-base" aria-hidden="true" />
            我的收藏
            {favorites.length > 0 && (
              <span className="rounded-full bg-accent-500 px-1.5 text-xs font-bold text-primary-950">
                {favorites.length}
              </span>
            )}
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          aria-expanded={menuOpen}
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 md:hidden">
          <form onSubmit={submitSearch} className="relative mb-3" role="search">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视名称…"
              aria-label="搜索影视名称"
              className="h-10 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-primary-50 text-primary-600" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}